<template>
    <div class="">
        <div class="flex justify-between items-center mb-6">
            <div></div>
        </div>

        <div class="flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow p-5 overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <!-- No Results State -->
                        <div v-if="!hasResults" class="text-center py-16">
                            <div class="max-w-md mx-auto">
                                <!-- Empty State Icon -->
                                <div class="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-gray-100 mb-6">
                                    <svg class="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                    </svg>
                                </div>
                                
                                <!-- No Results Message -->
                                <h3 class="text-lg font-semibold text-gray-900 mb-2">No Results Found</h3>
                                <p class="text-gray-600 mb-6">
                                    No data was found for the selected criteria. This could be due to:
                                </p>
                                
                                <!-- Possible Reasons -->
                                <div class="bg-blue-50 rounded-lg p-4 mb-6 text-left">
                                    <ul class="text-sm text-blue-800 space-y-2">
                                        <li class="flex items-start">
                                            <svg class="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <circle cx="10" cy="10" r="2"></circle>
                                            </svg>
                                            No transactions occurred during the selected date range
                                        </li>
                                        <li class="flex items-start">
                                            <svg class="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <circle cx="10" cy="10" r="2"></circle>
                                            </svg>
                                            The selected branch/agent had no activity
                                        </li>
                                        <li class="flex items-start">
                                            <svg class="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <circle cx="10" cy="10" r="2"></circle>
                                            </svg>
                                            Applied filters are too restrictive
                                        </li>
                                        <li class="flex items-start">
                                            <svg class="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <circle cx="10" cy="10" r="2"></circle>
                                            </svg>
                                            Data may not be available for the selected period
                                        </li>
                                    </ul>
                                </div>
                                
                                <!-- Report Details -->
                                <div class="bg-gray-50 rounded-lg p-4 mb-6">
                                    <h4 class="text-sm font-medium text-gray-900 mb-3">Current Report Settings:</h4>
                                    <div class="grid grid-cols-1 gap-2 text-sm text-gray-600">
                                        <div class="flex justify-between">
                                            <span class="font-medium">Report Type:</span>
                                            <span>{{ getReportDisplayName($route.params.reportOn) }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="font-medium">Date Range:</span>
                                            <span>{{ $route.params.from }} to {{ $route.params.to }}</span>
                                        </div>
                                        <div v-if="$route.params.branch" class="flex justify-between">
                                            <span class="font-medium">Branch:</span>
                                            <span>{{ $route.params.branch === 'all' ? 'All Branches' : $route.params.branch }}</span>
                                        </div>
                                        <div v-if="$route.params.staff" class="flex justify-between">
                                            <span class="font-medium">Staff:</span>
                                            <span>{{ $route.params.staff === 'all' ? 'All Staff' : $route.params.staff }}</span>
                                        </div>
                                        <div v-if="$route.params.agent || $route.params.superagent" class="flex justify-between">
                                            <span class="font-medium">Agent:</span>
                                            <span>{{ $route.params.superagent || $route.params.agent || 'All Agents' }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Action Buttons -->
                                <div class="flex flex-col sm:flex-row gap-3 justify-center">
                                    <button 
                                        @click="$router.go(-1)"
                                        class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                                        </svg>
                                        Modify Search
                                    </button>
                                    <button 
                                        @click="$router.push('/reports')"
                                        class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                        </svg>
                                        New Report
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div v-else id="element-to-print">
                            <template v-if="$route.params.reportOn === 'branch_sales_report'">
                                <NewBranchReport :resultData="$route.params.result" :fromDate="$route.params.from"
                                    :toDate="$route.params.to" />
                            </template>

                            <template v-if="$route.params.reportOn === 'branch_sales'">
                                <all-branch-sales v-if="$route.params.branch === 'all'" :data="$route.params.result" />
                                <branch-sales :showHeader="false" v-if="$route.params.branch >= 1 &&
                                    $route.params.staff == 'all'
                                    " :data="$route.params.result" />

                                <branch-staff-sales v-if="$route.params.branch >= 1 &&
                                    $route.params.staff >= 1
                                    " :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'deposits'">
                                <all-deposits-report v-if="$route.params.branch === 'all'" :data="$route.params.result" />
                                <deposits-report :showHeader="false" v-if="$route.params.branch >= 1 &&
                                    $route.params.staff == 'all'
                                    " :data="$route.params.result" />

                                <deposit-staff-report v-if="$route.params.branch >= 1 &&
                                    $route.params.staff >= 1
                                    " :data="$route.params.result" />
                            </template>
                            <template v-if="$route.params.reportOn === 'worksheets_report'">

                                <WorksheetsReportVue :data="$route.params.result" />
                            </template>
                            <template v-if="$route.params.reportOn === 'daily_cash_collection_report'">

                                <CashCollectionReport :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'float_replenishments'">
                                <FloatRepleshmentReport :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'account_replenishments'">
                                <AccountRepleshmentReport :data="$route.params.result" />
                            </template>


                            <template v-if="$route.params.reportOn === 'transactions'">
                                <transactions :data="$route.params.result.data" />
                            </template>

                            <template v-if="$route.params.reportOn === 'daily_sales'">
                                <daily-sales :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'tx_transactions'
                                ">
                                <TXTransactionReport :data="$route.params.result.data" />
                            </template>

                            <template v-if="$route.params.reportOn === 'agents_sales'">
                                <AgentTransactionsReports :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn ===
                                'commission_center'
                                ">
                                <BranchCommission v-if="$route.params.reportBy ===
                                    'chapman_branch'
                                    " :data="$route.params.result" />
                                <TxAgentsCommission v-if="$route.params.reportBy === 'tx_agents'
                                    " :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'revise_report'
                                ">
                                <ReviceReport :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'balance_report'
                                ">
                                <BalanceReport :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn ===
                                'superagents_sales'
                                ">
                                <SuperagentReport :data="$route.params.result" :showHeader="true" />
                            </template>

                            <template v-if="$route.params.reportOn === 'sms_report'">
                                <SmsReport :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'customer'">
                                <button @click="downloadDocument()"
                                    class="bg-green-400 px-6 py-2 rounded-md hover:bg-green-500 disabled:opacity-50">
                                    <span v-if="!isDownloading">Download XLSX</span>
                                    <span v-else>Downloading...</span>
                                </button>
                            </template>

                            <template v-if="$route.params.reportOn ===
                                'nawec_running_balance'
                                ">
                                <NawecRunningBalance :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn === 'nawec_recovery'">
                                <NawecRecoveryReport :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn ===
                                'denominator_report'
                                ">
                                <DenominatorReports :data="$route.params.result" />
                            </template>

                            <template v-if="$route.params.reportOn ===
                                'daily_cash_reconciliation_report'
                                ">
                                <CashReconciliationReport :data="$route.params.result" />
                            </template>
                            <template v-if="$route.params.reportOn ===
                                'financial_summary_report'
                                ">
                                <FinancialSummaryReport :data="$route.params.result" />
                            </template>
                            <template v-if="$route.params.reportOn ===
                                'nawec_sales_summary_report'
                                ">
                                <NawecSummaryReport :data="$route.params.result" />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import AllBranchSales from './Docs/AllBranchSales';
import FinancialSummaryReport from './Docs/FinancialSummaryReport';
import NawecSummaryReport from './Docs/NawecSummaryReport';
import DepositsReport from './Docs/DepositsReport';
import AllDepositsReport from './Docs/AllDepositsReport';
import DepositStaffReport from './Docs/DepositStaffReport';
import BranchSales from './Docs/BranchSales';
import BranchStaffSales from './Docs/BranchStaffSales.vue';
import BalanceReport from './Docs/BalanceReport.vue';
import DailySales from './Docs/DailySales.vue';
import ReviceReport from './Docs/ReviceReport.vue';
import Transactions from './Docs/Transactions.vue';
import SuperagentReport from './Docs/SuperagentReport.vue';
import TXTransactionReport from './Docs/TXTransactionReport.vue';
import AgentTransactionsReports from './Docs/AgentTransactionsReports.vue';
import SmsReport from './Docs/SmsReport.vue';

import BranchCommission from './Docs/BranchCommission.vue';
import TxAgentsCommission from './Docs/TxAgentsCommission.vue';
import NawecRunningBalance from './Docs/NawecRunningBalance.vue';
import NawecRecoveryReport from './Docs/NawecRecoveryReport.vue';
import CashReconciliationReport from './Docs/CashReconciliationReport.vue';
import DenominatorReports from './Docs/DenominatorReports.vue';
import NewBranchReport from './Docs/NewBranchReport.vue';
// import BranchSales from "../../components/Charts/BranchSales";
import WorksheetsReportVue from './Docs/WorksheetsReport.vue'
import CashCollectionReport from './Docs/CashCollectionReport.vue';
import FloatRepleshmentReport from './Docs/FloatRepleshmentReport.vue';
import AccountRepleshmentReport from './Docs/AccountRepleshmentReport.vue';
export default {
    components: {
        SmsReport,
        AllBranchSales,
        BranchSales,
        ReviceReport,
        AllDepositsReport,
        DepositStaffReport,
        BalanceReport,
        DepositsReport,
        BranchCommission,
        BranchStaffSales,
        DailySales,
        Transactions,
        CashReconciliationReport,
        SuperagentReport,
        TXTransactionReport,
        AgentTransactionsReports,
        TxAgentsCommission,
        NawecRunningBalance,
        NawecRecoveryReport,
        DenominatorReports,
        FinancialSummaryReport,
        NawecSummaryReport,
        WorksheetsReportVue,
        CashCollectionReport,
        NewBranchReport,
        FloatRepleshmentReport,
        AccountRepleshmentReport
    },

    middleware: 'auth',
    layout: 'default',

    data: () => ({
        branches: null,

        reportOn: '',
        from: '',
        to: '',

        isDownloading: false,
    }),

    computed: {
        hasResults() {
            const result = this.$route.params.result;
            
            // Check if result exists and has data
            if (!result) return false;
            
            // Handle different data structures
            if (Array.isArray(result)) {
                return result.length > 0;
            }
            
            if (typeof result === 'object') {
                // Check if it has a data property (common structure)
                if (result.data) {
                    return Array.isArray(result.data) ? result.data.length > 0 : !!result.data;
                }
                
                // Check if it's an object with keys (non-empty object)
                return Object.keys(result).length > 0;
            }
            
            // For primitive values, check if truthy
            return !!result;
        }
    },

    created() { },

    methods: {
        getReportDisplayName(reportType) {
            const reportNames = {
                'branch_sales_report': 'Branch Sales Report',
                'branch_sales': 'Branch Sales',
                'deposits': 'Deposits Report',
                'worksheets_report': 'Worksheets Report',
                'daily_cash_collection_report': 'Daily Cash Collection Report',
                'float_replenishments': 'Float Replenishments',
                'account_replenishments': 'Account Replenishments',
                'transactions': 'Transactions Report',
                'daily_sales': 'Daily Sales',
                'tx_transactions': 'TX Transactions',
                'agents_sales': 'Agents Sales',
                'commission_center': 'Commission Center',
                'revise_report': 'Revise Report',
                'balance_report': 'Balance Report',
                'superagents_sales': 'Super Agents Sales',
                'sms_report': 'SMS Report',
                'customer': 'Customer Report',
                'nawec_running_balance': 'NAWEC Running Balance',
                'nawec_recovery': 'NAWEC Recovery Report',
                'denominator_report': 'Denominator Report',
                'daily_cash_reconciliation_report': 'Daily Cash Reconciliation Report',
                'financial_summary_report': 'Financial Summary Report',
                'nawec_sales_summary_report': 'NAWEC Sales Summary Report'
            };
            
            return reportNames[reportType] || reportType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },

        getFileName() {
            return (
                this.$route.params.reportOn +
                '-' +
                this.$route.params.from +
                '-' +
                this.$route.params.to
            );
        },

        async downloadDocument() {
            axios({
                url: 'export/customers',
                method: 'GET',
                responseType: 'blob',
            }).then(response => {
                let blob = new Blob([response.data]);
                let link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = 'customers.xlsx';
                link.click();
            });
        },

        exportToPDF() {
            this.isLoading = true;
            var element = document.getElementById('element-to-print');
            var opt = {
                margin: 0,
                filename: `${this.getFileName()}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 1 },
                jsPDF: {
                    unit: 'in',
                    format: 'letter',
                    orientation: 'portrait',
                },
            };

            // New Promise-based usage:
            html2pdf().from(element).set(opt).save();

            // Old monolithic-style usage:
            html2pdf(element, opt);

            this.isLoading = false;
        },

        async generate() {
            this.isLoading = true;

            try {
                const { data } = await axios.get(
                    `/report/generate?from=${this.from}&to=${this.to}&report=${this.reportOn}`
                );

                this.report = data;
                this.isLoading = false;

                return true;
            } catch (e) {
                console.log('Error');
                this.isLoading = false;

                return false;
            }
        },
    },
};
</script>
